import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { format } from "date-fns";
import { buildDashboardUnifiedWebSocketUrl } from "../utils/webviewAudio";
import {
  DASHBOARD_WS_CONNECT_DELAYS,
  subscribeDashboardWebSocket,
} from "../utils/dashboardWebSocketManager";
import { useDashboardLive } from "./DashboardLiveContext";

const CHANNEL_KEY = "dashboard/unified";
const WS_CLOSED = typeof WebSocket !== "undefined" ? WebSocket.CLOSED : 3;
const WS_CONNECTING = typeof WebSocket !== "undefined" ? WebSocket.CONNECTING : 0;
const WS_OPEN = typeof WebSocket !== "undefined" ? WebSocket.OPEN : 1;

const DashboardWebSocketContext = createContext(null);

export function DashboardWebSocketProvider({ children }) {
  const { isLiveEnabled, isLoading } = useDashboardLive();
  const listenersRef = useRef(new Set());
  const socketApiRef = useRef(null);
  const pendingMessagesRef = useRef([]);
  const [readyState, setReadyState] = useState(WS_CLOSED);

  const notifyListeners = useCallback((payload) => {
    listenersRef.current.forEach((listener) => {
      try {
        listener(payload);
      } catch (err) {
        console.error("[Dashboard Unified WS] Listener error:", err);
      }
    });
  }, []);

  const flushPendingMessages = useCallback(() => {
    const api = socketApiRef.current;
    if (!api) {
      return;
    }
    const pending = pendingMessagesRef.current;
    pendingMessagesRef.current = [];
    pending.forEach((message) => {
      api.send(message);
    });
  }, []);

  useEffect(() => {
    if (isLoading || !isLiveEnabled) {
      if (socketApiRef.current) {
        socketApiRef.current.unsubscribe();
        socketApiRef.current = null;
        pendingMessagesRef.current = [];
      }
      setReadyState(WS_CLOSED);
      return undefined;
    }

    const today = format(new Date(), "yyyy-MM-dd");
    const tzOffset = new Date().getTimezoneOffset();

    const socketApi = subscribeDashboardWebSocket({
      channelKey: CHANNEL_KEY,
      connectDelayMs: DASHBOARD_WS_CONNECT_DELAYS.unified,
      buildUrl: () => buildDashboardUnifiedWebSocketUrl({
        pieDate: today,
        trafficDate: today,
        tzOffsetMinutes: tzOffset,
      }),
      onOpen: () => {
        setReadyState(WS_OPEN);
        flushPendingMessages();
        notifyListeners({ type: "__dashboard_ws_open" });
      },
      onMessage: (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type === "ping") {
            socketApiRef.current?.send?.({ type: "pong" });
            return;
          }
          notifyListeners(payload);
        } catch (err) {
          console.error("[Dashboard Unified WS] Failed to parse message:", err);
        }
      },
      onClose: (event) => {
        setReadyState(WS_CLOSED);
        if (event.code === 4401 || event.code === 4400 || event.code === 4403) {
          notifyListeners({ type: "dashboard_ws_error", code: event.code });
        }
      },
      onError: (err) => {
        console.error("[Dashboard Unified WS] Connection error:", err);
      },
    });

    socketApiRef.current = socketApi;
    const initialReadyState = socketApi.getReadyState?.() ?? WS_CONNECTING;
    setReadyState(initialReadyState === WS_CLOSED ? WS_CONNECTING : initialReadyState);

    return () => {
      socketApi.unsubscribe();
      socketApiRef.current = null;
      pendingMessagesRef.current = [];
      setReadyState(WS_CLOSED);
    };
  }, [isLiveEnabled, isLoading, flushPendingMessages, notifyListeners]);

  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const send = useCallback((message) => {
    if (!isLiveEnabled || isLoading) {
      return false;
    }

    if (socketApiRef.current?.send?.(message)) {
      return true;
    }

    const socketApi = socketApiRef.current;
    if (!socketApi) {
      pendingMessagesRef.current.push(message);
      return true;
    }

    const currentReadyState = socketApi.getReadyState?.() ?? WS_CLOSED;
    if (currentReadyState !== WS_OPEN) {
      pendingMessagesRef.current.push(message);
      return true;
    }

    return false;
  }, [isLiveEnabled, isLoading]);

  const getReadyState = useCallback(() => {
    if (!isLiveEnabled || isLoading) {
      return WS_CLOSED;
    }
    return socketApiRef.current?.getReadyState?.() ?? readyState;
  }, [isLiveEnabled, isLoading, readyState]);

  const value = useMemo(
    () => ({
      subscribe,
      send,
      getReadyState,
      isLiveEnabled,
      readyState,
      isSocketOpen: readyState === WS_OPEN,
    }),
    [subscribe, send, getReadyState, isLiveEnabled, readyState],
  );

  return (
    <DashboardWebSocketContext.Provider value={value}>
      {children}
    </DashboardWebSocketContext.Provider>
  );
}

export function useDashboardWebSocket() {
  const context = useContext(DashboardWebSocketContext);
  if (!context) {
    throw new Error("useDashboardWebSocket must be used within DashboardWebSocketProvider");
  }
  return context;
}
